import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { apiFetch } from '@/lib/http';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Package, Save, X, RefreshCw, Pencil, Check, Users } from 'lucide-react';

import { ReconciliationModal } from '@/components/campaigns/ReconciliationModal';
import { dbg } from '@/lib/dbg';
import { getCampaignIntegrityUserMessage } from '@/lib/campaignIntegrityErrors';

interface EditCampaignModalProps {
  campaignId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface Pack {
  id: number;
  name: string;
  description: string;
  retailerId: number;
  isActive: boolean;
  createdAt: string;
}

interface AvailablePacksResponse {
  availablePacks: Pack[];
  currentPackIds: number[];
  campaignStatus: number;
  hasCustomisedPacks?: boolean;
  debug?: {
    retailerIds: number[];
    totalPacks: number;
    activePacks: number;
    inactivePacks: number;
    inactivePackNames: string[];
  };
}

interface ManualGroup {
  groupId: string;
  groupName: string;
  storeIds: number[];
  formatEntries: Array<{
    mediaFormatId: number;
    quantity: number;
    coveragePercent?: number | null;
  }>;
  operatorTaskId?: number | null;
}

interface ManualGroupsResponse {
  success: boolean;
  groups: ManualGroup[];
}

const haveSameNumericMembership = (left: number[], right: number[]): boolean => {
  if (left.length !== right.length) return false;
  const rightSet = new Set(right);
  return left.every((value) => rightSet.has(value));
};

export function EditCampaignModal({ campaignId, isOpen, onClose, onSuccess }: EditCampaignModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCycleId, setSelectedCycleId] = useState<number | null>(null);
  const [selectedPackIds, setSelectedPackIds] = useState<number[]>([]);
  const [selectedAdvertiserId, setSelectedAdvertiserId] = useState<number | null>(null);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showDraftConfirmation, setShowDraftConfirmation] = useState(false);
  const [showReconciliationModal, setShowReconciliationModal] = useState(false);

  // Group rename state (for manual campaigns)
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingGroupName, setEditingGroupName] = useState('');

  // Fetch campaign basic info (includes campaignType)
  const { data: campaign, isLoading: campaignLoading } = useQuery({
    queryKey: ["/api/campaigns", campaignId],
    queryFn: async () => {
      const res = await apiFetch(`/api/campaigns/${campaignId}`);
      if (!res.ok) throw new Error('Failed to fetch campaign');
      return res.json();
    },
    enabled: isOpen
  });

  // Determine campaign type (API may return campaign_type or campaignType)
  const campaignType = campaign?.campaignType || campaign?.campaign_type;
  // Match server-side logic: both 'manual' and 'pack_manual' are treated as manual campaigns
  const isManualCampaign = campaignType === 'manual' || campaignType === 'pack_manual';
  const isPackCampaign = campaignType === 'pack' || (!campaignType && !isManualCampaign);

  // Populate form fields when campaign data loads
  useEffect(() => {
    if (campaign) {
      setName(campaign.name || '');
      setDescription(campaign.description || '');
      setSelectedCycleId(campaign.cycleId || null);
      setSelectedAdvertiserId(campaign.advertiserId || null);
    }
  }, [campaign]);

  // Fetch available cycles
  const { data: cycles, isLoading: cyclesLoading } = useQuery({
    queryKey: ['/api/booking-cycles'],
    queryFn: async () => {
      const res = await apiFetch('/api/booking-cycles');
      if (!res.ok) throw new Error('Failed to fetch cycles');
      return res.json();
    },
    enabled: isOpen
  });

  // Fetch available advertisers
  const { data: advertisers = [], isLoading: advertisersLoading } = useQuery({
    queryKey: ['/api/admin/advertisers'],
    queryFn: async () => {
      const res = await apiFetch('/api/admin/advertisers');
      if (!res.ok) throw new Error('Failed to fetch advertisers');
      const json = await res.json();
      return json?.data?.advertisers || [];
    },
    enabled: isOpen
  });

  // Fetch available packs and current selection - ONLY for PACK campaigns
  const { data: packsData, isLoading: packsLoading, error: packsError } = useQuery<AvailablePacksResponse>({
    queryKey: [`/api/campaigns/${campaignId}/available-packs`],
    queryFn: async () => {
      console.log(`[EditCampaignModal] Fetching available packs for campaign ${campaignId}`);
      const res = await apiFetch(`/api/campaigns/${campaignId}/available-packs`);
      if (!res.ok) {
        const errorText = await res.text();
        console.error(`[EditCampaignModal] Available packs API error:`, res.status, errorText);
        throw new Error(`Failed to fetch available packs: ${res.status} ${errorText}`);
      }
      const data = await res.json();
      console.log(`[EditCampaignModal] Available packs response:`, data);
      return data;
    },
    // CRITICAL: Only fetch packs for PACK campaigns, NOT manual campaigns
    enabled: isOpen && isPackCampaign && !campaignLoading
  });

  // Fetch groups for manual campaigns using unified groups-view endpoint
  const { data: groupsViewData, isLoading: groupsLoading, refetch: refetchGroups } = useQuery({
    queryKey: [`/api/campaigns/${campaignId}/groups-view`],
    queryFn: async () => {
      const res = await apiFetch(`/api/campaigns/${campaignId}/groups-view`);
      if (!res.ok) {
        throw new Error('Failed to fetch campaign groups');
      }
      return res.json();
    },
    enabled: isOpen && isManualCampaign && !campaignLoading,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // Populate selected packs when packs data loads (pack campaigns only)
  useEffect(() => {
    if (packsData && isPackCampaign) {
      setSelectedPackIds(packsData.currentPackIds || []);
      console.log('Packs data loaded:', packsData);
    }
  }, [packsData, isPackCampaign]);

  // Reset form when modal is closed
  useEffect(() => {
    if (!isOpen) {
      // Only reset confirmation states, not form data
      setShowConfirmation(false);
      setShowDraftConfirmation(false);
      setEditingGroupId(null);
      setEditingGroupName('');
    }
  }, [isOpen]);

  // Group rename mutation
  const renameGroupMutation = useMutation({
    mutationFn: async ({ groupId, groupName }: { groupId: string; groupName: string }) => {
      const response = await apiRequest(
        `/api/campaigns/${campaignId}/manual-groups/${groupId}/rename`,
        'PATCH',
        { groupName }
      );
      const data = await response.json();
      if (!response.ok) {
        // Handle special case where groups were backfilled and we need to refetch
        if (data.backfilled) {
          // Refetch and ask user to retry
          queryClient.invalidateQueries({ queryKey: [`/api/campaigns/${campaignId}/groups-view`] });
          refetchGroups();
          throw new Error('Groups were restored. Please try renaming again.');
        }
        throw new Error(data.error || 'Failed to rename group');
      }
      return data;
    },
    onSuccess: (data) => {
      const message = data.wasBackfilled 
        ? 'Groups restored and renamed successfully.' 
        : 'Group name updated successfully.';
      toast({
        title: 'Group Renamed',
        description: message,
      });
      setEditingGroupId(null);
      setEditingGroupName('');
      // Refetch to get updated data - invalidate all relevant endpoints
      queryClient.invalidateQueries({ queryKey: [`/api/campaigns/${campaignId}`] });
      queryClient.invalidateQueries({ queryKey: [`/api/campaigns/${campaignId}/groups-view`] });
      queryClient.invalidateQueries({ queryKey: ['/api/campaigns', campaignId, 'groups-view'] });
      queryClient.invalidateQueries({ queryKey: [`/api/campaigns/${campaignId}/sitelist-data`] });
      // Also refetch the groups in this modal
      refetchGroups();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to rename group',
        variant: 'destructive',
      });
    }
  });

  const normalizedCurrentPackIds = [...(packsData?.currentPackIds || [])].sort((a, b) => a - b);
  const normalizedSelectedPackIds = [...selectedPackIds].sort((a, b) => a - b);
  const nameChanged = name.trim() !== (campaign?.name || '').trim();
  const descriptionChanged = description !== (campaign?.description || '');
  const advertiserChanged = selectedAdvertiserId !== (campaign?.advertiserId ?? null);
  const cycleChanged = selectedCycleId !== (campaign?.cycleId ?? null);
  const packSelectionChanged = isPackCampaign
    ? !haveSameNumericMembership(normalizedSelectedPackIds, normalizedCurrentPackIds)
    : false;
  const hasMetadataChanges = nameChanged || descriptionChanged || advertiserChanged;
  const isMetadataOnlySave = hasMetadataChanges && !cycleChanged && !packSelectionChanged;

  const refreshCampaignQueries = () => {
    queryClient.refetchQueries({ queryKey: [`/api/campaigns/${campaignId}`] });
    queryClient.refetchQueries({ queryKey: ["/api/campaigns", campaignId] });
    queryClient.refetchQueries({ queryKey: ["/api/campaigns", campaignId, "summary"] });
    queryClient.invalidateQueries({ queryKey: ['/api/campaigns'] });
  };

  const closeAfterSuccessfulSave = () => {
    setShowConfirmation(false);
    onClose();
    onSuccess?.();
  };

  const updateMetadataMutation = useMutation({
    mutationFn: async () => {
      dbg.log('[EditCampaignModal] Updating metadata only', {
        campaignId,
        nameChanged,
        descriptionChanged,
        advertiserChanged,
      });

      const payload: Record<string, unknown> = {};

      if (nameChanged) payload.name = name.trim();
      if (descriptionChanged) payload.description = description;
      if (advertiserChanged) payload.advertiserId = selectedAdvertiserId;

      const response = await apiRequest(
        `/api/campaigns/${campaignId}/meta`,
        'PATCH',
        payload,
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update campaign metadata');
      }

      return response.json();
    },
    onSuccess: () => {
      refreshCampaignQueries();
      toast({
        title: 'Changes Saved',
        description: 'Campaign metadata has been updated successfully.',
      });
      closeAfterSuccessfulSave();
    },
    onError: (error: Error) => {
      dbg.error('[EditCampaignModal] Metadata update failed', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update campaign metadata',
        variant: 'destructive',
      });
      setShowConfirmation(false);
    },
  });

  // Create draft revision mutation
  const createDraftMutation = useMutation({
    mutationFn: async () => {
      dbg.log('[EditCampaignModal] Creating draft revision', {
        campaignId,
        selectedPackIds,
        name,
        isManualCampaign
      });

      const payload: any = {
        name,
        description,
        cycleId: selectedCycleId,
        advertiserId: selectedAdvertiserId
      };

      // Only include packIds for PACK campaigns
      if (isPackCampaign) {
        payload.packIds = selectedPackIds;
      }

      const response = await apiRequest(
        `/api/campaigns/${campaignId}/revise`,
        'POST',
        payload
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create draft revision');
      }

      return response.json();
    },
    onSuccess: () => {
      refreshCampaignQueries();

      setShowConfirmation(false);

      // Manual campaigns are updated directly by /revise (no draft snapshot created)
      // so we skip the confirmation step and close the modal
      if (isManualCampaign) {
        toast({
          title: 'Changes Saved',
          description: 'Campaign changes have been saved successfully.',
        });
        onClose();
        onSuccess?.();
      } else {
        // Pack campaigns create a draft snapshot that needs confirmation
        toast({
          title: 'Draft Saved',
          description: 'Draft revision created successfully. Click "Confirm Changes" to make it active.',
        });
        setShowDraftConfirmation(true);
      }
    },
    onError: (error: Error) => {
      dbg.error('[EditCampaignModal] Draft creation failed', error);
      toast({
        title: 'Error',
        description: getCampaignIntegrityUserMessage(error, 'revise'),
        variant: 'destructive',
      });
      setShowConfirmation(false);
    }
  });

  // Confirm draft revision mutation
  const confirmDraftMutation = useMutation({
    mutationFn: async () => {
      dbg.log('[EditCampaignModal] Confirming draft revision', { campaignId });

      const response = await apiRequest(
        `/api/campaigns/${campaignId}/confirm`,
        'PATCH'
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to confirm draft revision');
      }

      return response.json();
    },
    onSuccess: () => {
      toast({
        title: 'Changes Confirmed',
        description: 'Campaign changes have been confirmed and are now active.',
      });

      refreshCampaignQueries();
      queryClient.invalidateQueries({ queryKey: ['mediaSummarySnapshot', campaignId] });

      setShowDraftConfirmation(false);
      onClose();
      onSuccess?.();
    },
    onError: (error: Error) => {
      dbg.error('[EditCampaignModal] Draft confirmation failed', error);
      toast({
        title: 'Error',
        description: getCampaignIntegrityUserMessage(error, 'confirm'),
        variant: 'destructive',
      });
    }
  });



  const handlePackToggle = (packId: number) => {
    setSelectedPackIds(prev =>
      prev.includes(packId)
        ? prev.filter(id => id !== packId)
        : [...prev, packId]
    );
  };

  const handleSave = () => {
    if (isManualCampaign && cycleChanged) {
      toast({
        title: 'Cycle Change Not Supported Here',
        description: 'Use the structural edit flow for cycle reassignment. Quick edit only supports metadata changes for manual campaigns.',
        variant: 'destructive',
      });
      return;
    }

    if (!hasMetadataChanges && !cycleChanged && !packSelectionChanged) {
      toast({
        title: 'No Changes',
        description: 'There are no changes to save.',
      });
      return;
    }

    // For PACK campaigns, require at least one pack
    if (isPackCampaign && selectedPackIds.length === 0) {
      toast({
        title: 'No Packs Selected',
        description: 'Please select at least one pack',
        variant: 'destructive',
      });
      return;
    }

    setShowConfirmation(true);
  };

  const handleConfirmSave = () => {
    if (isMetadataOnlySave || isManualCampaign) {
      updateMetadataMutation.mutate();
      return;
    }

    createDraftMutation.mutate();
  };

  const handleConfirmDraft = () => {
    confirmDraftMutation.mutate();
  };

  const handleCancel = () => {
    if (showDraftConfirmation) {
      setShowDraftConfirmation(false);
    } else if (showConfirmation) {
      setShowConfirmation(false);
    } else {
      onClose();
    }
  };

  // Group rename handlers
  const handleStartGroupRename = (groupId: string, currentName: string) => {
    setEditingGroupId(groupId);
    setEditingGroupName(currentName);
  };

  const handleSaveGroupRename = () => {
    if (!editingGroupId || !editingGroupName.trim()) {
      toast({
        title: 'Error',
        description: 'Group name cannot be empty',
        variant: 'destructive',
      });
      return;
    }
    renameGroupMutation.mutate({
      groupId: editingGroupId,
      groupName: editingGroupName.trim()
    });
  };

  const handleCancelGroupRename = () => {
    setEditingGroupId(null);
    setEditingGroupName('');
  };

  // Loading state
  if (campaignLoading || (isPackCampaign && packsLoading)) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <span className="ml-2">Loading campaign...</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Check if campaign is a draft (statusId = 1)
  // Use packsData.campaignStatus for pack campaigns, campaign.statusId for manual
  const isDraft = isPackCampaign
    ? Number(packsData?.campaignStatus ?? campaign?.statusId) === 1
    : Number(campaign?.statusId) === 1;

  console.log('[EditCampaignModal] Debug:', {
    campaignId,
    campaignType,
    rawCampaignType: campaign?.campaign_type,
    isManualCampaign,
    isPackCampaign,
    rawCampaignStatus: campaign?.statusId,
    packsDataStatus: packsData?.campaignStatus,
    parsedDraftStatus: isDraft,
  });

  if (!isDraft) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cannot Edit Campaign</DialogTitle>
          </DialogHeader>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Only draft campaigns can be edited. This campaign has status ID: {campaign?.statusId}
            </AlertDescription>
          </Alert>
          <DialogFooter>
            <Button onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const packChanges = isPackCampaign ? {
    added: selectedPackIds.filter(id => !packsData?.currentPackIds.includes(id)),
    removed: packsData?.currentPackIds.filter(id => !selectedPackIds.includes(id)) || []
  } : { added: [], removed: [] };

  // Extract groups for manual campaigns from groups-view endpoint
  const manualGroups: ManualGroup[] = [];
  // Use groupsEditable flag from API response
  // If groupsEditable is false, groups are derived from snapshot but rename is still allowed
  // (the server will backfill groups_json on first rename)
  const groupsEditable = groupsViewData?.groupsEditable ?? false;
  const groupsEditableReason = groupsViewData?.groupsEditableReason;
  
  if (isManualCampaign && groupsViewData?.groups) {
    for (const group of groupsViewData.groups) {
      manualGroups.push({
        groupId: group.groupId || group.groupKey, // Use groupId if available, fallback to groupKey
        groupName: group.groupName,
        storeIds: group.storeIds || [],
        formatEntries: group.formatEntries || []
      });
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle>
            Edit Campaign
            {isManualCampaign && (
              <Badge variant="secondary" className="ml-2">Manual</Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {isManualCampaign
              ? 'Edit campaign metadata and group names. Use Detailed Edit for store/format changes.'
              : isMetadataOnlySave
                ? 'Edit campaign metadata. Saving will not regenerate the campaign snapshot.'
                : 'Make changes to your draft campaign. Saving will regenerate the campaign snapshot.'
            }
          </DialogDescription>
        </DialogHeader>

        {showDraftConfirmation ? (
          // Draft confirmation screen
          <div className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="font-medium">
                Confirm Campaign Changes?
              </AlertDescription>
            </Alert>

            <div className="space-y-2 text-sm">
              <p>Your draft revision has been saved. Confirming will:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Make the draft revision active</li>
                <li>Archive the previous campaign version</li>
                <li>Update all stores, media formats, tasks, and assignments</li>
                <li>Make changes visible in cycle locking and reporting</li>
              </ul>
            </div>

            <p className="text-sm text-muted-foreground">
              This action cannot be undone. The previous campaign version will be archived.
            </p>

            <DialogFooter>
              <Button variant="outline" onClick={handleCancel} disabled={confirmDraftMutation.isPending}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDraft}
                disabled={confirmDraftMutation.isPending}
              >
                {confirmDraftMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Confirming...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Confirm Changes
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        ) : showConfirmation ? (
          // Save draft confirmation screen
          <div className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="font-medium">
                {isMetadataOnlySave || isManualCampaign ? 'Save Campaign Changes?' : 'Save Draft Revision?'}
              </AlertDescription>
            </Alert>

            <div className="space-y-2 text-sm">
              <p>{isMetadataOnlySave || isManualCampaign ? 'This will update:' : 'This will create a draft revision with:'}</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                {isPackCampaign && !isMetadataOnlySave && (
                  <>
                    <li><strong>{selectedPackIds.length}</strong> packs selected (was {packsData?.currentPackIds.length || 0})</li>
                    {packChanges.added.length > 0 && (
                      <li className="text-green-600">+{packChanges.added.length} pack(s) added</li>
                    )}
                    {packChanges.removed.length > 0 && (
                      <li className="text-red-600">-{packChanges.removed.length} pack(s) removed</li>
                    )}
                  </>
                )}
                {name !== campaign?.name && (
                  <li className="text-blue-600">Campaign name updated</li>
                )}
                {descriptionChanged && (
                  <li className="text-blue-600">Campaign description updated</li>
                )}
                {advertiserChanged && (
                  <li className="text-blue-600">Advertiser assignment updated</li>
                )}
                {selectedCycleId !== campaign?.cycleId && (
                  <li className="text-blue-600">Cycle assignment updated</li>
                )}
              </ul>
            </div>

            <p className="text-sm text-muted-foreground">
              {isMetadataOnlySave || isManualCampaign
                ? 'These changes will be saved immediately without regenerating the campaign snapshot.'
                : 'The draft will be saved but not activated until you confirm the changes.'}
            </p>

            <DialogFooter>
              <Button variant="outline" onClick={handleCancel} disabled={createDraftMutation.isPending || updateMetadataMutation.isPending}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmSave}
                disabled={createDraftMutation.isPending || updateMetadataMutation.isPending}
              >
                {createDraftMutation.isPending || updateMetadataMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {isMetadataOnlySave || isManualCampaign ? 'Saving Changes...' : 'Saving Draft...'}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {isMetadataOnlySave || isManualCampaign ? 'Save Changes' : 'Save Draft'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          // Edit form
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Basic Information</h3>

              <div className="space-y-2">
                <Label htmlFor="campaign-name">Campaign Name</Label>
                <Input
                  id="campaign-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter campaign name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="campaign-description">Description</Label>
                <Textarea
                  id="campaign-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter campaign description"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="campaign-cycle">Cycle Assignment</Label>
                {cyclesLoading ? (
                  <div className="text-sm text-muted-foreground">Loading cycles...</div>
                ) : (
                  <Select
                    value={selectedCycleId?.toString() || ''}
                    onValueChange={(value) => setSelectedCycleId(value ? parseInt(value) : null)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a cycle" />
                    </SelectTrigger>
                    <SelectContent>
                      {cycles?.map((cycle: any) => (
                        <SelectItem key={cycle.id} value={cycle.id.toString()}>
                          {cycle.name} ({new Date(cycle.startDate).toLocaleDateString()} - {new Date(cycle.endDate).toLocaleDateString()})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="campaign-advertiser">Advertiser Assignment</Label>
                {advertisersLoading ? (
                  <div className="text-sm text-muted-foreground">Loading advertisers...</div>
                ) : (
                  <Select
                    value={selectedAdvertiserId?.toString() || ''}
                    onValueChange={(value) => setSelectedAdvertiserId(value ? parseInt(value) : null)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select an advertiser" />
                    </SelectTrigger>
                    <SelectContent>
                      {advertisers.sort((a: any, b: any) => a.name.localeCompare(b.name)).map((advertiser: any) => (
                        <SelectItem key={advertiser.id} value={advertiser.id.toString()}>
                          {advertiser.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            <Separator />

            {/* PACK CAMPAIGNS: Pack Selection */}
            {isPackCampaign && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Pack Selection</h3>
                  <Badge variant="secondary">
                    {selectedPackIds.length} selected
                  </Badge>
                </div>

                {packsLoading ? (
                  <div className="text-sm text-muted-foreground">Loading packs...</div>
                ) : packsError ? (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <div className="space-y-2">
                        <p className="font-medium">Error loading packs</p>
                        <p className="text-sm">{packsError.message}</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => queryClient.invalidateQueries({ queryKey: [`/api/campaigns/${campaignId}/available-packs`] })}
                        >
                          Retry
                        </Button>
                      </div>
                    </AlertDescription>
                  </Alert>
                ) : packsData?.availablePacks && packsData.availablePacks.length > 0 ? (
                  <div className="space-y-2 max-h-96 overflow-y-auto border rounded-lg p-3">
                    {packsData.availablePacks.map((pack) => {
                      const isSelected = selectedPackIds.includes(pack.id);
                      const wasSelected = packsData.currentPackIds.includes(pack.id);
                      const isNew = isSelected && !wasSelected;
                      const isRemoved = !isSelected && wasSelected;

                      return (
                        <div
                          key={pack.id}
                          className={`flex items-start space-x-3 p-3 rounded-lg border ${isSelected ? 'bg-blue-50 border-blue-200' : 'hover:bg-gray-50'
                            }`}
                        >
                          <Checkbox
                            id={`pack-${pack.id}`}
                            checked={isSelected}
                            onCheckedChange={() => handlePackToggle(pack.id)}
                          />
                          <div className="flex-1 min-w-0">
                            <label
                              htmlFor={`pack-${pack.id}`}
                              className="flex items-center gap-2 font-medium cursor-pointer"
                            >
                              <Package className="h-4 w-4 flex-shrink-0" />
                              <span>{pack.name}</span>
                              {isNew && (
                                <Badge variant="success">New</Badge>
                              )}
                              {isRemoved && (
                                <Badge variant="destructive">Removed</Badge>
                              )}
                              {wasSelected && isSelected && !isNew && (
                                <Badge variant="secondary">Current</Badge>
                              )}
                            </label>
                            {pack.description && (
                              <p className="text-sm text-muted-foreground mt-1">
                                {pack.description}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      No active packs available for this retailer.
                    </AlertDescription>
                  </Alert>
                )}

                {packChanges.added.length > 0 || packChanges.removed.length > 0 ? (
                  <Alert className="bg-amber-50 border-amber-200">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-800">
                      <strong>Pack changes detected:</strong>
                      {packChanges.added.length > 0 && ` +${packChanges.added.length} added`}
                      {packChanges.added.length > 0 && packChanges.removed.length > 0 && ', '}
                      {packChanges.removed.length > 0 && ` -${packChanges.removed.length} removed`}
                    </AlertDescription>
                  </Alert>
                ) : null}
              </div>
            )}

            {/* MANUAL CAMPAIGNS: Groups Section (Read-Only with Rename) */}
            {isManualCampaign && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Store Groups
                  </h3>
                  <Badge variant="secondary">
                    {manualGroups.length} group{manualGroups.length !== 1 ? 's' : ''}
                  </Badge>
                </div>

                {groupsEditable ? (
                  <Alert className="bg-blue-50 border-blue-200">
                    <AlertCircle className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800">
                      <strong>Manual Campaign:</strong> Group rename is available here. For store/format changes, use Detailed Edit.
                    </AlertDescription>
                  </Alert>
                ) : manualGroups.length > 0 ? (
                  <Alert className="bg-amber-50 border-amber-200">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-800">
                      <strong>Groups derived from snapshot.</strong> Renaming a group will restore editable groups for this campaign.
                    </AlertDescription>
                  </Alert>
                ) : null}

                {groupsLoading ? (
                  <div className="text-sm text-muted-foreground">Loading groups...</div>
                ) : manualGroups.length > 0 ? (
                  <div className="space-y-2 border rounded-lg p-3">
                    {manualGroups.map((group) => (
                      <div
                        key={group.groupId}
                        className="flex items-center justify-between p-3 rounded-lg border bg-gray-50"
                      >
                        {editingGroupId === group.groupId ? (
                          <div className="flex items-center gap-2 flex-1">
                            <Input
                              value={editingGroupName}
                              onChange={(e) => setEditingGroupName(e.target.value)}
                              className="max-w-xs"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveGroupRename();
                                if (e.key === 'Escape') handleCancelGroupRename();
                              }}
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={handleSaveGroupRename}
                              disabled={renameGroupMutation.isPending}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={handleCancelGroupRename}
                              disabled={renameGroupMutation.isPending}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-2">
                              <Users className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{group.groupName}</span>
                              <Badge variant="outline" className="text-xs">
                                {group.storeIds?.length || '?'} stores
                              </Badge>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleStartGroupRename(group.groupId, group.groupName)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      No groups found for this manual campaign.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {/* Info/warning boxes */}
            {isPackCampaign && packsData?.hasCustomisedPacks ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>This campaign has customised packs.</strong> Regenerating the snapshot here will NOT update operator assignments. Use Detailed Edit to modify customised campaigns or un-customise the packs first.
                </AlertDescription>
              </Alert>
            ) : isPackCampaign ? (
              <Alert className="bg-blue-50 border-blue-200">
                <AlertCircle className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  <strong>Operator assignments changed?</strong> Use "Reconcile Assignments" to update system-derived operators while preserving your manual edits.
                </AlertDescription>
              </Alert>
            ) : null}

            <DialogFooter className="flex justify-between">
              {/* Reconcile Button - Only for PACK campaigns */}
              {isPackCampaign && (
                <Button
                  variant="secondary"
                  onClick={() => setShowReconciliationModal(true)}
                  disabled={selectedPackIds.length === 0}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reconcile Assignments
                </Button>
              )}
              {isManualCampaign && <div />} {/* Spacer for manual campaigns */}
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleCancel}>
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSave}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Draft
                </Button>
              </div>
            </DialogFooter>
          </div>
        )}
      </DialogContent>

      <ReconciliationModal
        campaignId={campaignId}
        isOpen={showReconciliationModal}
        onClose={() => setShowReconciliationModal(false)}
      />
    </Dialog>
  );
}
